import React, { useEffect, useState } from "react";
import "./LocalVideoPreview.css";

/**
 * Preview of a video the member has just picked or recorded, before upload.
 *
 * A bare <video> with no poster is what Android's WebView draws as a large grey
 * play glyph — no frame of the clip and, without controls, no way to play it
 * (client, 03/10/2026). Here the first frame is captured from the local file as
 * the poster, and the native controls let the member watch what they are about
 * to post. Used by every composer that accepts a video.
 */

// Shown until the real frame is ready, so the WebView never draws its own
// placeholder in the meantime.
const BLANK_POSTER =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

const POSTER_MAX_WIDTH = 720;
const POSTER_TIMEOUT_MS = 8000;

/**
 * The frame at ~0.1 s of a same-origin (blob:) video, as a JPEG data URL, or
 * null when the platform will not decode it in time. Never throws.
 */
export const captureVideoPoster = (src, { timeoutMs = POSTER_TIMEOUT_MS } = {}) =>
  new Promise((resolve) => {
    if (!src || typeof document === "undefined") {
      resolve(null);
      return;
    }

    const video = document.createElement("video");
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      video.removeAttribute("src");
      try {
        video.load();
      } catch {}
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);

    video.muted = true;
    video.playsInline = true;
    video.setAttribute("playsinline", "");
    video.preload = "auto";

    video.onloadeddata = () => {
      const target = Number.isFinite(video.duration) && video.duration > 0
        ? Math.min(0.1, video.duration / 2)
        : 0;
      if (target > 0) {
        video.currentTime = target;
      } else {
        video.onseeked();
      }
    };
    video.onseeked = () => {
      try {
        const width = video.videoWidth;
        const height = video.videoHeight;
        if (!width || !height) {
          finish(null);
          return;
        }
        const scale = Math.min(1, POSTER_MAX_WIDTH / width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(width * scale);
        canvas.height = Math.round(height * scale);
        canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
        finish(canvas.toDataURL("image/jpeg", 0.8));
      } catch {
        finish(null);
      }
    };
    video.onerror = () => finish(null);

    video.src = src;
    try {
      video.load();
    } catch {}
  });

export default function LocalVideoPreview({ src, className = "" }) {
  const [poster, setPoster] = useState(BLANK_POSTER);

  useEffect(() => {
    setPoster(BLANK_POSTER);
    if (!src) return undefined;
    let cancelled = false;
    captureVideoPoster(src).then((frame) => {
      if (!cancelled && frame) setPoster(frame);
    });
    return () => {
      cancelled = true;
    };
  }, [src]);

  if (!src) return null;

  return (
    <video
      className={`local-video-preview ${className}`.trim()}
      src={src}
      poster={poster}
      controls
      playsInline
      preload="metadata"
      controlsList="nodownload noplaybackrate"
      disablePictureInPicture
      // The composers open the picker or remove the clip on a tap around the
      // preview; using the player must do neither.
      onClick={(event) => event.stopPropagation()}
    />
  );
}
